import React from "react";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, RadarChart, PolarGrid, PolarAngleAxis, Radar } from "recharts";
import { Clock, Target, Workflow, Flame, Trophy, Swords, Radar as RadarIcon, ArrowRight } from "lucide-react";

const RADAR_DIM_LABELS = { structure: "Structure", clarity: "Clarity", confidence: "Confidence", problem_solving: "Problem-solving", communication: "Communication" };

export default function AnalyticsTab({ analytics, interviewAnalytics }) {
  if (!analytics) return null;
  const stats = [
    { label: "Learning Hours", value: analytics.learning_hours, icon: Clock },
    { label: "Avg Quiz Score", value: `${analytics.avg_quiz_score}%`, icon: Target },
    { label: "Avg Simulator Score", value: `${analytics.avg_simulator_score}%`, icon: Workflow },
    { label: "Current Streak", value: analytics.streak_current, icon: Flame },
  ];
  return (
    <div className="space-y-6" data-testid="analytics-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="glass-panel rounded-xl p-4 text-center" data-testid={`analytics-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
              <Icon className="w-4 h-4 text-indigo-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-white">{s.value}</p>
              <p className="text-[10px] text-slate-400 uppercase mt-0.5">{s.label}</p>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5" data-testid="analytics-weekly-chart">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Weekly Progress</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={analytics.weekly_progress}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="week" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="completed" fill="#FB923C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="glass-panel rounded-2xl p-5" data-testid="analytics-monthly-chart">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Monthly Progress</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={analytics.monthly_progress}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="completed" fill="#a78bfa" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-400 mb-3">Strong Topics</p>
          {analytics.strong_topics.length === 0 && <p className="text-xs text-slate-400">Complete more quizzes to see strong topics.</p>}
          {analytics.strong_topics.map((t) => (
            <div key={t.day} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
              <span className="text-slate-300">Day {t.day} — {t.title}</span><span className="text-emerald-400 font-semibold">{t.score}%</span>
            </div>
          ))}
        </div>
        <div className="glass-panel rounded-2xl p-5">
          <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-3">Weak Topics</p>
          {analytics.weak_topics.length === 0 && <p className="text-xs text-slate-400">No weak topics detected yet — nice work!</p>}
          {analytics.weak_topics.map((t) => (
            <div key={t.day} className="flex items-center justify-between text-sm py-1.5 border-b border-white/5 last:border-0">
              <span className="text-slate-300">Day {t.day} — {t.title}</span><span className="text-red-400 font-semibold">{t.score}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-5 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2 text-sm text-slate-300"><Trophy className="w-4 h-4 text-[#FBBF24]" /> Leaderboard Rank #{analytics.leaderboard_position}</div>
        <div className="flex items-center gap-2 text-sm text-slate-300"><Target className="w-4 h-4 text-indigo-400" /> {analytics.challenge_completion_pct}% Challenges Complete</div>
        {analytics.duel_stats && <div className="flex items-center gap-2 text-sm text-slate-300"><Swords className="w-4 h-4 text-rose-400" /> Duel Rating {analytics.duel_stats.rating} ({analytics.duel_stats.wins}W-{analytics.duel_stats.losses}L)</div>}
      </div>

      <div className="glass-panel rounded-2xl p-5" data-testid="analytics-interview-skill-radar">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><RadarIcon className="w-3.5 h-3.5 text-primary" /> Interview Skill Radar</p>
          <Link to="/interview" data-testid="analytics-practice-interview-link" className="flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary/80">
            Practice Interviews <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {interviewAnalytics?.sample_size > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-center">
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={Object.entries(interviewAnalytics.skill_radar).map(([k, v]) => ({ dim: RADAR_DIM_LABELS[k], value: v ?? 0 }))} outerRadius="72%">
                  <PolarGrid stroke="rgba(255,255,255,0.1)" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 10 }} />
                  <Radar dataKey="value" stroke="#5b9dff" fill="#5b9dff" fillOpacity={0.35} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <p className="text-xs text-slate-400" data-testid="analytics-interview-caption">
                Based on your last {interviewAnalytics.sample_size} interview{interviewAnalytics.sample_size === 1 ? "" : "s"} across all tracks.
              </p>
              {interviewAnalytics.percentile != null && (
                <p className="text-sm text-white mt-2">You're scoring higher than <span className="text-primary font-semibold">{interviewAnalytics.percentile}%</span> of candidates on this track.</p>
              )}
              {interviewAnalytics.insight_text && (
                <p className="text-xs text-emerald-300 mt-2">{interviewAnalytics.insight_text}</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-400 py-4 text-center">Complete a mock interview in the Interview Center to unlock your Skill Radar — technical depth, communication, confidence & more.</p>
        )}
      </div>
    </div>
  );
}
